<script setup>
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { OnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import WahaIntegrationsAPI from 'dashboard/api/wahaIntegrations';

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);

const wahaInboxes = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'Channel::Api' &&
      inbox.inbox_identifier?.startsWith('waha_')
  )
);

const selectedInboxId = ref(null);
const sessionStatus = ref('');
const loadingStatus = ref(false);
const publishing = ref(false);

const activeTab = ref('text');
const statusText = ref('');
const backgroundColor = ref('#38b42f');
const selectedFont = ref(1);
const caption = ref('');
const mediaFile = ref(null);
const mediaPreview = ref(null);
const mediaUrl = ref('');

const statuses = ref([]);
const loadingStatuses = ref(false);
const deletingStatusId = ref(null);
const viewingStatus = ref(null);
const emojiPickerFor = ref(null);

const onSelectEmoji = ({ value }) => {
  if (emojiPickerFor.value === 'caption') {
    caption.value += value;
  } else {
    statusText.value += value;
  }
};

const statusColors = [
  '#38b42f',
  '#1fae59',
  '#00a5f4',
  '#2178fa',
  '#7364f2',
  '#a567ff',
  '#f0318a',
  '#e74c3c',
  '#f39c12',
  '#2b2b2b',
];

// Aproximacion CSS de las fuentes de estado de WhatsApp (WAHA font 1-5)
// Orden WhatsApp: Sans, Serif, Norican (script), Bryndan (handwriting), Oswald (condensada)
const fonts = [
  { value: 1, family: "'Helvetica Neue', Arial, sans-serif" },
  { value: 2, family: "Georgia, 'Times New Roman', serif" },
  { value: 3, family: "'Norican', 'Segoe Script', cursive" },
  { value: 4, family: "'Comic Sans MS', 'Segoe Print', cursive" },
  {
    value: 5,
    family: "'Oswald', 'Arial Narrow', 'Franklin Gothic Medium', sans-serif",
  },
];

const fontFamily = font => {
  const found = fonts.find(f => f.value === Number(font));
  return found ? found.family : fonts[0].family;
};

const tabs = computed(() => [
  { key: 'text', label: t('WAHA_STATUS.TABS.TEXT'), icon: 'i-lucide-type' },
  { key: 'image', label: t('WAHA_STATUS.TABS.IMAGE'), icon: 'i-lucide-image' },
  { key: 'video', label: t('WAHA_STATUS.TABS.VIDEO'), icon: 'i-lucide-video' },
  { key: 'voice', label: t('WAHA_STATUS.TABS.VOICE'), icon: 'i-lucide-mic' },
]);

const selectedInbox = computed(() =>
  wahaInboxes.value.find(i => i.id === selectedInboxId.value)
);

const isConnected = computed(() => sessionStatus.value === 'WORKING');

const canPublish = computed(() => {
  if (!isConnected.value || publishing.value) return false;
  if (activeTab.value === 'text') return statusText.value.trim().length > 0;
  return !!mediaFile.value || !!mediaUrl.value.trim();
});

const loadSessionStatus = async () => {
  if (!selectedInbox.value) return;
  loadingStatus.value = true;
  sessionStatus.value = '';
  try {
    const response = await WahaIntegrationsAPI.getSessionStatus(
      selectedInbox.value.inbox_identifier
    );
    sessionStatus.value = response.data?.status || 'UNKNOWN';
  } catch {
    sessionStatus.value = 'ERROR';
  } finally {
    loadingStatus.value = false;
  }
};

const loadStatuses = async () => {
  if (!selectedInbox.value) return;
  loadingStatuses.value = true;
  try {
    const response = await WahaIntegrationsAPI.getStatuses(
      selectedInbox.value.inbox_identifier
    );
    statuses.value = Array.isArray(response.data)
      ? response.data.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      : [];
  } catch {
    statuses.value = [];
  } finally {
    loadingStatuses.value = false;
  }
};

const removeStatus = async status => {
  if (!selectedInbox.value) return;
  deletingStatusId.value = status.id;
  try {
    const response = await WahaIntegrationsAPI.deleteStatus(
      selectedInbox.value.inbox_identifier,
      status.id
    );
    if (response.data?.deleted) {
      statuses.value = statuses.value.filter(s => s.id !== status.id);
    } else {
      await loadStatuses();
      setTimeout(loadStatuses, 4000);
    }
    useAlert(t('WAHA_STATUS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('WAHA_STATUS.DELETE_ERROR'));
  } finally {
    deletingStatusId.value = null;
  }
};

const statusPreviewText = status => {
  const text = status.body || status.caption;
  if (text) return text;
  if (status.mediaType && status.mediaType !== 'text') {
    return t(`WAHA_STATUS.TABS.${status.mediaType.toUpperCase()}`);
  }
  return t('WAHA_STATUS.MEDIA_STATUS');
};

const statusMediaUrl = status => {
  // Media devuelta por WAHA (engines que exponen el store)
  const media = status.media;
  if (media?.data) {
    return `data:${media.mimetype || 'image/jpeg'};base64,${media.data}`;
  }
  // Registro local: attachment de ActiveStorage o URL externa
  return status.mediaUrl || null;
};

const isVideoStatus = status => {
  const mime = status.media?.mimetype || '';
  return status.mediaType === 'video' || mime.startsWith('video/');
};

const isVoiceStatus = status => {
  const mime = status.media?.mimetype || '';
  return status.mediaType === 'voice' || mime.startsWith('audio/');
};

const statusStateLabel = status =>
  t(
    `WAHA_STATUS.STATE.${(status.publicationStatus || 'published').toUpperCase()}`
  );

const formatStatusDate = timestamp => {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

watch(selectedInboxId, () => {
  statuses.value = [];
  loadSessionStatus();
  loadStatuses();
});

const onFileChange = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  mediaFile.value = file;
  mediaUrl.value = '';
  const reader = new FileReader();
  reader.onload = e => {
    mediaPreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const clearMedia = () => {
  mediaFile.value = null;
  mediaPreview.value = null;
};

const fileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const publish = async () => {
  if (!selectedInbox.value) return;
  const sessionName = selectedInbox.value.inbox_identifier;
  publishing.value = true;
  try {
    if (activeTab.value === 'text') {
      await WahaIntegrationsAPI.sendTextStatus(sessionName, {
        text: statusText.value,
        backgroundColor: backgroundColor.value,
        font: selectedFont.value,
      });
    } else {
      const payload = { caption: caption.value };
      if (mediaUrl.value.trim()) {
        payload.fileUrl = mediaUrl.value.trim();
      } else if (mediaFile.value) {
        payload.fileData = await fileToBase64(mediaFile.value);
        payload.fileName = mediaFile.value.name;
        payload.fileMimetype = mediaFile.value.type;
      }
      if (activeTab.value === 'voice') {
        payload.backgroundColor = backgroundColor.value;
      }
      await WahaIntegrationsAPI.sendMediaStatus(
        sessionName,
        activeTab.value,
        payload
      );
    }
    useAlert(t('WAHA_STATUS.PUBLISH_SUCCESS'));
    statusText.value = '';
    caption.value = '';
    clearMedia();
    await loadStatuses();
    setTimeout(loadStatuses, 4000);
    setTimeout(loadStatuses, 10000);
  } catch (error) {
    useAlert(error.response?.data?.error || t('WAHA_STATUS.PUBLISH_ERROR'));
  } finally {
    publishing.value = false;
  }
};

const acceptForTab = computed(() => {
  switch (activeTab.value) {
    case 'image':
      return 'image/*';
    case 'video':
      return 'video/mp4';
    case 'voice':
      return 'audio/*';
    default:
      return '';
  }
});

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto">
    <div class="w-full max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
      <h1 class="text-2xl font-semibold text-n-slate-12 mb-1">
        {{ $t('WAHA_STATUS.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11 mb-6">
        {{ $t('WAHA_STATUS.DESCRIPTION') }}
      </p>

      <div
        v-if="!wahaInboxes.length"
        class="p-8 text-center border border-dashed border-n-weak rounded-xl"
      >
        <p class="text-n-slate-11">
          {{ $t('WAHA_STATUS.NO_INBOXES') }}
        </p>
      </div>

      <template v-else>
        <!-- Selector de bandeja -->
        <div class="mb-6">
          <label
            class="block text-xs font-medium text-n-slate-11 mb-1.5 uppercase tracking-wide"
          >
            {{ $t('WAHA_STATUS.SELECT_INBOX') }}
          </label>
          <div class="flex items-center gap-3">
            <select
              v-model="selectedInboxId"
              class="flex-1 max-w-md px-3 py-2 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 text-sm"
            >
              <option :value="null" disabled>
                {{ $t('WAHA_STATUS.SELECT_INBOX_PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in wahaInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <span
              v-if="selectedInboxId"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full"
              :class="
                isConnected
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-amber-3 text-n-amber-11'
              "
            >
              <span
                class="w-1.5 h-1.5 rounded-full"
                :class="isConnected ? 'bg-n-teal-9' : 'bg-n-amber-9'"
              />
              {{
                loadingStatus
                  ? $t('WAHA_STATUS.CHECKING')
                  : isConnected
                    ? $t('WAHA_STATUS.CONNECTED')
                    : $t('WAHA_STATUS.NOT_CONNECTED')
              }}
            </span>
          </div>
        </div>

        <template v-if="selectedInboxId">
          <!-- Tabs -->
          <div class="flex gap-1 border-b border-n-weak mb-5">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              class="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors"
              :class="
                activeTab === tab.key
                  ? 'border-woot-500 text-woot-500'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeTab = tab.key"
            >
              <span :class="tab.icon" class="w-4 h-4" />
              {{ tab.label }}
            </button>
          </div>

          <div
            class="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_340px] xl:gap-12"
          >
            <!-- Formulario -->
            <div class="space-y-4">
              <!-- Texto -->
              <template v-if="activeTab === 'text'">
                <div>
                  <label
                    class="block text-xs font-medium text-n-slate-11 mb-1.5"
                  >
                    {{ $t('WAHA_STATUS.TEXT_LABEL') }}
                  </label>
                  <div class="flex items-end gap-2">
                    <textarea
                      v-model="statusText"
                      rows="3"
                      class="flex-1 px-3 py-2 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 text-sm resize-none"
                      :placeholder="$t('WAHA_STATUS.TEXT_PLACEHOLDER')"
                    />
                    <OnClickOutside
                      class="relative"
                      @trigger="emojiPickerFor = null"
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        color="slate"
                        icon="i-lucide-smile-plus"
                        @click="
                          emojiPickerFor =
                            emojiPickerFor === 'text' ? null : 'text'
                        "
                      />
                      <EmojiIconPicker
                        v-if="emojiPickerFor === 'text'"
                        mode="emoji"
                        class="top-10 end-0"
                        @select="onSelectEmoji"
                      />
                    </OnClickOutside>
                  </div>
                </div>
                <div>
                  <label
                    class="block text-xs font-medium text-n-slate-11 mb-1.5"
                  >
                    {{ $t('WAHA_STATUS.BACKGROUND') }}
                  </label>
                  <div class="flex gap-2 flex-wrap">
                    <button
                      v-for="color in statusColors"
                      :key="color"
                      type="button"
                      class="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
                      :class="
                        backgroundColor === color
                          ? 'border-n-slate-12'
                          : 'border-transparent'
                      "
                      :style="{ backgroundColor: color }"
                      @click="backgroundColor = color"
                    />
                  </div>
                </div>
                <div>
                  <label
                    class="block text-xs font-medium text-n-slate-11 mb-1.5"
                  >
                    {{ $t('WAHA_STATUS.FONT') }}
                  </label>
                  <div class="flex gap-2">
                    <button
                      v-for="font in fonts"
                      :key="font.value"
                      type="button"
                      class="w-9 h-9 rounded-lg border text-base transition-colors"
                      :class="
                        selectedFont === font.value
                          ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-500'
                          : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
                      "
                      :style="{ fontFamily: font.family }"
                      @click="selectedFont = font.value"
                    >
                      {{ 'Aa' }}
                    </button>
                  </div>
                </div>
              </template>

              <!-- Media -->
              <template v-else>
                <div>
                  <label
                    class="block text-xs font-medium text-n-slate-11 mb-1.5"
                  >
                    {{ $t('WAHA_STATUS.MEDIA_LABEL') }}
                  </label>
                  <input
                    type="file"
                    :accept="acceptForTab"
                    class="block w-full text-sm text-n-slate-11 file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-woot-500 file:text-white file:text-sm file:font-medium hover:file:bg-woot-600 file:cursor-pointer"
                    @change="onFileChange"
                  />
                  <div class="mt-2 flex items-center gap-2">
                    <span class="text-xs text-n-slate-10">
                      {{ $t('WAHA_STATUS.OR_URL') }}
                    </span>
                    <input
                      v-model="mediaUrl"
                      type="url"
                      class="flex-1 px-3 py-1.5 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 text-sm"
                      :placeholder="$t('WAHA_STATUS.URL_PLACEHOLDER')"
                      @input="mediaUrl.trim() && clearMedia()"
                    />
                  </div>
                  <p
                    v-if="activeTab === 'video'"
                    class="text-xs text-n-amber-11 mt-2"
                  >
                    {{ $t('WAHA_STATUS.VIDEO_FORMAT_NOTE') }}
                  </p>
                  <p
                    v-if="activeTab === 'voice'"
                    class="text-xs text-n-amber-11 mt-2"
                  >
                    {{ $t('WAHA_STATUS.VOICE_FORMAT_NOTE') }}
                  </p>
                </div>
                <div v-if="activeTab !== 'voice'">
                  <label
                    class="block text-xs font-medium text-n-slate-11 mb-1.5"
                  >
                    {{ $t('WAHA_STATUS.CAPTION') }}
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      v-model="caption"
                      type="text"
                      class="flex-1 px-3 py-2 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 text-sm"
                      :placeholder="$t('WAHA_STATUS.CAPTION_PLACEHOLDER')"
                    />
                    <OnClickOutside
                      class="relative"
                      @trigger="emojiPickerFor = null"
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        color="slate"
                        icon="i-lucide-smile-plus"
                        @click="
                          emojiPickerFor =
                            emojiPickerFor === 'caption' ? null : 'caption'
                        "
                      />
                      <EmojiIconPicker
                        v-if="emojiPickerFor === 'caption'"
                        mode="emoji"
                        class="top-10 end-0"
                        @select="onSelectEmoji"
                      />
                    </OnClickOutside>
                  </div>
                </div>
                <div v-if="activeTab === 'voice'">
                  <label
                    class="block text-xs font-medium text-n-slate-11 mb-1.5"
                  >
                    {{ $t('WAHA_STATUS.BACKGROUND') }}
                  </label>
                  <div class="flex gap-2 flex-wrap">
                    <button
                      v-for="color in statusColors"
                      :key="color"
                      type="button"
                      class="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
                      :class="
                        backgroundColor === color
                          ? 'border-n-slate-12'
                          : 'border-transparent'
                      "
                      :style="{ backgroundColor: color }"
                      @click="backgroundColor = color"
                    />
                  </div>
                </div>
              </template>

              <Button
                :label="$t('WAHA_STATUS.PUBLISH')"
                :is-loading="publishing"
                :disabled="!canPublish"
                class="w-full justify-center"
                @click="publish"
              />
            </div>

            <!-- Vista previa estilo WhatsApp -->
            <div
              class="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-6"
            >
              <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
                {{ $t('WAHA_STATUS.PREVIEW') }}
              </label>
              <div
                class="aspect-[9/16] w-full max-w-[270px] max-h-[480px] mx-auto rounded-2xl overflow-hidden flex items-center justify-center relative border border-n-weak shadow-sm"
                :style="{ backgroundColor: backgroundColor }"
              >
                <template v-if="activeTab === 'text'">
                  <p
                    class="text-white text-center px-8 text-2xl leading-snug break-words"
                    :style="{ fontFamily: fontFamily(selectedFont) }"
                  >
                    {{ statusText || $t('WAHA_STATUS.TEXT_PLACEHOLDER') }}
                  </p>
                </template>
                <template v-else-if="activeTab === 'image' && mediaPreview">
                  <img
                    :src="mediaPreview"
                    class="absolute inset-0 w-full h-full object-cover"
                    alt=""
                  />
                  <p
                    v-if="caption"
                    class="absolute bottom-0 inset-x-0 bg-black/50 text-white text-sm px-4 py-2"
                  >
                    {{ caption }}
                  </p>
                </template>
                <template v-else-if="activeTab === 'video' && mediaPreview">
                  <video
                    :src="mediaPreview"
                    class="absolute inset-0 w-full h-full object-cover"
                    controls
                  />
                </template>
                <template v-else-if="activeTab === 'voice'">
                  <div
                    class="flex flex-col items-center gap-4 text-white px-6 w-full"
                  >
                    <div class="flex items-center gap-2">
                      <span class="i-lucide-mic w-8 h-8" />
                      <div class="flex gap-1 items-end h-8">
                        <span
                          v-for="n in 20"
                          :key="n"
                          class="w-1 bg-white/80 rounded-full"
                          :style="{ height: `${8 + ((n * 7) % 20)}px` }"
                        />
                      </div>
                    </div>
                    <audio
                      v-if="mediaPreview || mediaUrl"
                      :src="mediaPreview || mediaUrl"
                      controls
                      class="w-full"
                    />
                  </div>
                </template>
                <template v-else>
                  <p class="text-white/60 text-sm">
                    {{ $t('WAHA_STATUS.PREVIEW_EMPTY') }}
                  </p>
                </template>
              </div>
            </div>

            <!-- Lista de estados publicados -->
            <div class="mt-2 lg:col-start-1 lg:row-start-2">
              <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-semibold text-n-slate-12">
                  {{ $t('WAHA_STATUS.PUBLISHED_TITLE') }}
                </h2>
                <Button
                  sm
                  slate
                  icon="i-lucide-refresh-cw"
                  :is-loading="loadingStatuses"
                  :label="$t('WAHA_STATUS.REFRESH')"
                  @click="loadStatuses"
                />
              </div>

              <div
                v-if="loadingStatuses && !statuses.length"
                class="p-6 text-center text-sm text-n-slate-11"
              >
                {{ $t('WAHA_STATUS.LOADING') }}
              </div>

              <div
                v-else-if="!statuses.length"
                class="p-6 text-center border border-dashed border-n-weak rounded-xl text-sm text-n-slate-11"
              >
                {{ $t('WAHA_STATUS.EMPTY_LIST') }}
              </div>

              <div
                v-else
                class="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4"
              >
                <div
                  v-for="status in statuses"
                  :key="status.id"
                  class="group relative aspect-[9/16] rounded-xl overflow-hidden border border-n-weak flex items-center justify-center cursor-pointer"
                  :style="{
                    backgroundColor: status.backgroundColor || undefined,
                  }"
                  :class="{ 'bg-n-solid-2': !status.backgroundColor }"
                  @click="viewingStatus = status"
                >
                  <div
                    v-if="isVoiceStatus(status)"
                    class="flex flex-col items-center justify-center gap-3 w-full px-3 text-n-slate-11"
                    :class="{ 'text-white': status.backgroundColor }"
                  >
                    <span class="i-lucide-mic w-8 h-8" />
                    <audio
                      v-if="statusMediaUrl(status)"
                      :src="statusMediaUrl(status)"
                      controls
                      class="w-full"
                      @click.stop
                    />
                  </div>
                  <video
                    v-else-if="statusMediaUrl(status) && isVideoStatus(status)"
                    :src="statusMediaUrl(status)"
                    class="absolute inset-0 w-full h-full object-cover"
                    controls
                    playsinline
                    @click.stop
                  />
                  <img
                    v-else-if="statusMediaUrl(status)"
                    :src="statusMediaUrl(status)"
                    class="absolute inset-0 w-full h-full object-cover"
                    alt=""
                  />
                  <div
                    v-else-if="isVideoStatus(status)"
                    class="flex flex-col items-center gap-2 text-n-slate-11 px-3"
                  >
                    <span class="i-lucide-video w-8 h-8" />
                    <p
                      v-if="statusPreviewText(status)"
                      class="text-xs text-center break-words line-clamp-3"
                    >
                      {{ statusPreviewText(status) }}
                    </p>
                  </div>
                  <p
                    v-else
                    class="text-xs text-center px-3 break-words line-clamp-6"
                    :class="
                      status.backgroundColor
                        ? 'text-white font-medium'
                        : 'text-n-slate-11'
                    "
                    :style="{ fontFamily: fontFamily(status.font) }"
                  >
                    {{ statusPreviewText(status) }}
                  </p>
                  <span
                    v-if="status.mediaType && status.mediaType !== 'text'"
                    class="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/50 text-white text-[10px] uppercase"
                  >
                    {{ status.mediaType }}
                  </span>
                  <span
                    v-if="status.publicationStatus !== 'published'"
                    class="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px]"
                    :title="status.errorMessage || ''"
                  >
                    {{ statusStateLabel(status) }}
                  </span>
                  <div
                    class="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1.5 flex items-center justify-between"
                  >
                    <span class="text-[10px] text-white/90">
                      {{ formatStatusDate(status.timestamp) }}
                    </span>
                    <button
                      type="button"
                      :disabled="
                        deletingStatusId === status.id ||
                        ['pending', 'deleting'].includes(
                          status.publicationStatus
                        )
                      "
                      class="text-white/80 hover:text-red-400 transition-colors disabled:opacity-50"
                      :title="$t('WAHA_STATUS.DELETE')"
                      @click.stop="removeStatus(status)"
                    >
                      <span
                        v-if="deletingStatusId === status.id"
                        class="i-lucide-loader-2 w-4 h-4 animate-spin block"
                      />
                      <span v-else class="i-lucide-trash-2 w-4 h-4 block" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- Visor de estado en grande -->
      <div
        v-if="viewingStatus"
        class="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        @click.self="viewingStatus = null"
      >
        <div
          class="relative rounded-2xl overflow-hidden flex items-center justify-center"
          :class="{
            'aspect-[9/16] h-[90vh] max-w-[90vw]':
              !statusMediaUrl(viewingStatus),
          }"
          :style="{
            backgroundColor: viewingStatus.backgroundColor || '#111',
          }"
        >
          <template v-if="isVoiceStatus(viewingStatus)">
            <div class="flex flex-col items-center gap-6 text-white px-8 w-80">
              <span class="i-lucide-mic w-16 h-16" />
              <audio
                v-if="statusMediaUrl(viewingStatus)"
                :src="statusMediaUrl(viewingStatus)"
                controls
                autoplay
                class="w-full"
              />
            </div>
          </template>
          <video
            v-else-if="
              statusMediaUrl(viewingStatus) && isVideoStatus(viewingStatus)
            "
            :src="statusMediaUrl(viewingStatus)"
            class="max-h-[90vh] max-w-[90vw] object-contain"
            controls
            autoplay
          />
          <img
            v-else-if="statusMediaUrl(viewingStatus)"
            :src="statusMediaUrl(viewingStatus)"
            class="max-h-[90vh] max-w-[90vw] object-contain"
            alt=""
          />
          <p
            v-else
            class="text-white text-center px-8 text-2xl break-words font-medium"
            :style="{ fontFamily: fontFamily(viewingStatus.font) }"
          >
            {{ statusPreviewText(viewingStatus) }}
          </p>
          <button
            type="button"
            class="absolute top-3 right-3 text-white/80 hover:text-white bg-black/50 rounded-full p-2"
            @click="viewingStatus = null"
          >
            <span class="i-lucide-x w-5 h-5 block" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=Norican&family=Oswald:wght@500&display=swap');
</style>
